// SPDX-License-Identifier: AGPL-3.0-or-later
import DeveloperOptions from '@app/features/devtools/state/DeveloperOptions';
import * as ImageCacheUtils from '@app/features/messaging/utils/ImageCacheUtils';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {thumbHashToDataURL} from 'thumbhash';
export interface MediaLoadingState {
	loaded: boolean;
	error: boolean;
	thumbHashURL?: string;
	onLoad: () => void;
	onError: () => void;
}
export function useMediaLoading(src: string, placeholder?: string): MediaLoadingState {
	const [loadingState, setLoadingState] = useState<Pick<MediaLoadingState, 'error' | 'loaded'>>({
		loaded: ImageCacheUtils.hasImage(src),
		error: false,
	});
	const shouldForcePlaceholder = DeveloperOptions.forceRenderPlaceholders || DeveloperOptions.forceMediaLoading;
	const thumbHashURL = useMemo(() => {
		if (!placeholder) return;
		try {
			const bytes = Uint8Array.from(atob(placeholder), (c) => c.charCodeAt(0));
			return thumbHashToDataURL(bytes);
		} catch {
			return;
		}
	}, [placeholder]);
	useEffect(() => {
		const isCached = ImageCacheUtils.hasImage(src);
		if (shouldForcePlaceholder) {
			setLoadingState({loaded: false, error: false});
			return;
		}
		setLoadingState({loaded: isCached, error: false});
		if (isCached) return;
		let isCurrent = true;
		ImageCacheUtils.loadImage(
			src,
			() => {
				if (!isCurrent) return;
				setLoadingState({loaded: true, error: false});
			},
			() => {
				if (!isCurrent) return;
				setLoadingState((currentState) => (currentState.loaded ? currentState : {loaded: false, error: true}));
			},
		);
		return () => {
			isCurrent = false;
		};
	}, [shouldForcePlaceholder, src]);
	const onLoad = useCallback(() => {
		setLoadingState({loaded: true, error: false});
	}, []);
	const onError = useCallback(() => {
		setLoadingState((prev) => (prev.loaded ? prev : {loaded: false, error: true}));
	}, []);
	return {...loadingState, thumbHashURL, onLoad, onError};
}
